// CustomerConfigureView — WHERE THE CUSTOMER BUILDS THEIR PROJECT.
//
// Alex, 2026-08-12: "the customer can still have to create its own tree of
// functions and add a high-level product". Then, on how it starts and where it
// lives: "Can we do empty now, and later add Templates choices?" and "Same
// project but different forms."
//
// So: the SAME project rows the internal screen edits, through a much smaller
// door. Not a copy, not a snapshot — a different form over the same data.
//
// ⚠ SAME ONE RULE AS CustomerView: this file may fetch `/api/customer/...` and
// NOTHING ELSE, and imports no cost helper. scripts/check-customer-view.mjs
// enforces both on this file as well as on CustomerView.jsx.
//
// ⚠ AND THE RULE A WRITE SCREEN ADDS: the customer write routes answer through
// src/customer-view.js, never with the internal row they just wrote. A route that
// saves and then echoes the internal object is a read endpoint wearing a POST.
// That is enforced server-side (see /api/customer/... in server-web.js) because
// this file cannot enforce it — which is exactly why it is written down twice.
//
// EMPTY, NOT TEMPLATED (Alex's answer to "what does the customer start from").
// Templates come later and change only what this screen is SEEDED with; nothing
// here assumes a starting shape, so adding them will not rewrite this file.
//
// 3D IS HERE, AND THE QUOTA SHAPES IT (CV-3, Alex 2026-08-12). Reading a model
// never calls Onshape — a cached model is bytes off disk, so clicking around costs
// nothing. Generating one is a separate, explicit act that ASKS first and states
// what it spends. INOVIU_ONSHAPE_FAKE=1 serves a sample so this can be built and
// tested without touching the quota at all; INOVIU_ONSHAPE_LIVE=1 is what permits
// a real export, and without it the route refuses.
//
// NO AUTH YET (INF-4). Nothing here decides WHOSE project this is. A write
// surface makes that gap matter more than a read one did.

import { useCallback, useEffect, useMemo, useState } from 'react';
import HierarchyWorkspace from './components/HierarchyWorkspace.jsx';
import ViewSwitcher from './components/ViewSwitcher.jsx';
import SectionTitle from './components/SectionTitle.jsx';
import Notice from './components/Notice.jsx';
import Text from './components/Text.jsx';
import CascadeSelect, { useCascadeSignal } from './components/CascadeSelect.jsx';
// format.js formats numbers it is handed; it computes nothing and reaches into no
// row for a price. See the note in CustomerView.jsx — what the gate forbids is
// format-cols.jsx and anything that computes.
import { qtyDetail } from './format.js';
import { DELETE, WARN } from './icons.js';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_GLYPH, BTN_DISABLED } from './theme-tokens.js';
import Model3D from './components/Model3D.jsx';

// One place that turns a fetch into either data or a readable error, so eleven
// call sites do not each invent their own half of it.
//
// ⚠ THE `/api/customer/` PREFIX IS WRITTEN OUT AT EVERY CALL SITE, and that
// repetition is deliberate. A `const API = '/api/customer'` reads better and is
// INVISIBLE to scripts/check-customer-view.mjs, which greps this file for fetch
// string literals — the gate would see an empty prefix, pass, and one edit to
// that constant could later point the whole screen at an internal route. Caught
// by the gate when this file was first written with the constant.
async function call(url, options) {
  const r = await fetch(url, options);
  if (r.ok) return r.status === 204 ? null : r.json();
  let msg = `Request failed (${r.status})`;
  try { const e = await r.json(); if (e?.error) msg = e.error; } catch { /* not JSON */ }
  throw new Error(msg);
}

const json = (body) => ({
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
});
const patch = (body) => ({ ...json(body), method: 'PATCH' });

function toTreeNodes(fns) {
  return fns.map(f => ({
    id: `func:${f.id}`,
    code: f.code || '',
    label: f.label || '',
    children: toTreeNodes(f.children || []),
  }));
}

function findFn(fns, id) {
  for (const f of fns) {
    if (`func:${f.id}` === id) return f;
    const hit = findFn(f.children || [], id);
    if (hit) return hit;
  }
  return null;
}

// ---------------------------------------------------------------------------
// One configuration value, editable.
//
// ⚠ THE FIELD IS UNCONTROLLED WHILE BEING TYPED. Writing every keystroke back
// through the server would re-resolve the placement on each character and fight
// the caret; committing on blur/Enter is also what makes "typical" meaningful —
// a value is not overridden until the customer has finished saying so.
// ---------------------------------------------------------------------------
function ParamEditor({ p, onCommit, disabled }) {
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);

  const shown = p.text != null && p.text !== ''
    ? p.text
    : (typeof p.value === 'number' ? qtyDetail(p.value) : p.value);

  // A parameter with TEXT is a size chosen from a list ('3" (80)'), not a number
  // to type into. Those need the size picker, which is not in this build — shown
  // read-only rather than offering an edit that would silently do the wrong thing.
  const editable = !disabled && (p.text == null || p.text === '');

  const commit = () => {
    setEditing(false);
    const n = Number(draft);
    if (draft.trim() === '' || !Number.isFinite(n) || n === p.value) return;
    onCommit(n);
  };

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', fontSize: 'var(--fs-small)', marginBottom: 2 }}>
      <span style={{ color: 'var(--th-text-secondary)', minWidth: 110 }}>{p.name}</span>
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
          style={{
            width: 80, fontSize: 'var(--fs-small)', fontFamily: 'inherit',
            padding: '1px 4px', border: 'var(--tg-filter-border)', borderRadius: 3,
            fontVariantNumeric: 'tabular-nums',
          }}
        />
      ) : (
        <button
          type="button"
          disabled={!editable}
          onClick={() => { setDraft(String(p.value ?? '')); setEditing(true); }}
          title={editable ? 'Click to change' : 'Chosen from a list — not editable here yet'}
          style={{
            border: 'none', background: 'transparent', padding: 0,
            font: 'inherit', fontVariantNumeric: 'tabular-nums',
            fontWeight: p.overridden ? 600 : 400,
            color: 'var(--th-text-body)', cursor: editable ? 'pointer' : 'default',
            textDecoration: editable ? 'underline dotted' : 'none',
          }}
        >
          {shown == null || shown === '' ? '—' : String(shown)}{p.unit ? ` ${p.unit}` : ''}
        </button>
      )}
      {/* Weight and the WORD, never colour — Alex is colour-blind and the
          codebase convention is "shape = meaning; no colour signal". */}
      {!p.overridden && (
        <span style={{ color: 'var(--th-text-secondary)', fontSize: 'var(--fs-micro)' }}>typical</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// THE 3D PANEL (CV-3, answered by Alex 2026-08-12: "Need a save or place
// configuration and the warning about the API call… we might to fake test as
// well", and the default model is "the one on cache").
//
// ⚠ IT LIVES IN THIS FILE ON PURPOSE. check-customer-view.mjs scans exactly two
// files for API paths; a component in its own file could fetch an internal route
// and the gate would never see it. Anything on the customer side that talks to the
// server belongs where the gate can read it.
//
// THREE STATES, and the quota decides all three:
//   linked + cached    render it. Serving a cached model costs NOTHING — the bytes
//                      come off disk and the route has no path to Onshape at all.
//   linked + no cache  offer an export, and ASK FIRST.
//   not linked         say so plainly. This is the normal case today: all five
//                      placeable products are unlinked.
//
// ⚠ THE ASK IS INLINE, NOT A BROWSER CONFIRM. ERR-1 is explicit that an error or a
// question never arrives in a grey browser box detached from what caused it, and
// window.confirm is on the retire list, not the menu.
function ModelPanel({ placementId }) {
  const [state, setState] = useState(null);
  const [asking, setAsking] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [reloadAt, setReloadAt] = useState(0);

  useEffect(() => {
    let alive = true;
    call(`/api/customer/model/${placementId}`)
      .then(s => { if (alive) setState(s); })
      .catch(e => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [placementId, reloadAt]);

  const src = `/api/customer/model/${placementId}/glb?v=${reloadAt}`;

  const doExport = async () => {
    setWorking(true); setError(null);
    try {
      await call(`/api/customer/model/${placementId}/refresh`, { method: 'POST' });
      setAsking(false);
      setReloadAt(n => n + 1);
    } catch (e) { setError(e.message); }
    finally { setWorking(false); }
  };

  const note = (t) => (
    <Text kind="note" as="div" style={{ fontStyle: 'italic' }}>{t}</Text>
  );

  if (error) return <Notice kind="warning" onDismiss={() => setError(null)}>{error}</Notice>;
  if (!state) return note('Checking for a 3D model…');
  if (!state.linked) return note('No 3D model for this item yet.');

  return (
    <div style={{ marginTop: 8 }}>
      {state.cached ? (
        <ModelFrame src={src} fake={state.fake} />
      ) : (
        <>
          {note('No 3D model has been generated for this configuration yet.')}
          {!asking ? (
            <button
              type="button" onClick={() => setAsking(true)}
              style={{ ...BTN_SECONDARY, marginTop: 6, fontFamily: 'inherit' }}
            >Generate the 3D model</button>
          ) : (
            // THE WARNING IS THE FEATURE (Alex: "the warning about the API call").
            // It names the cost in the units that are actually scarce, because a
            // quota is only defensible if the person spending it can see it go.
            <div style={{
              marginTop: 6, padding: '8px 10px', borderRadius: 3,
              border: '1px solid var(--th-border)', background: 'var(--th-row-even)', maxWidth: 620,
            }}>
              <Text kind="small" as="div" style={{ marginBottom: 6 }}>
                Generating this model exports it from Onshape — about <strong>3 calls</strong> out
                of a yearly allowance of roughly <strong>5,000</strong>. It is kept afterwards, so
                viewing it again is free.
              </Text>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button" onClick={doExport} disabled={working}
                  style={{ ...BTN_PRIMARY, fontFamily: 'inherit', ...(working ? BTN_DISABLED : null) }}
                >{working ? 'Generating…' : 'Generate it'}</button>
                <button
                  type="button" onClick={() => setAsking(false)} disabled={working}
                  style={{ ...BTN_SECONDARY, fontFamily: 'inherit', ...(working ? BTN_DISABLED : null) }}
                >Not now</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// THE 3D view (Model3D), same as every internal surface: Onshape gestures and the
// Perspective toggle. Model3D takes the URL from here and names no route itself, so
// the customer-view gate still sees every /api/ path this screen can reach.
function ModelFrame({ src, fake }) {
  return (
    <div>
      {fake && (
        <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-text-secondary)', marginBottom: 2 }}>
          {WARN} SAMPLE MODEL — fake mode, not this item's geometry
        </div>
      )}
      <Model3D src={src} height={300} maxWidth={620} background="var(--th-viewer-bg)" />
    </div>
  );
}

function PlacementBlock({ p, onConfigure, onRemove, busy }) {
  return (
    <div style={{
      border: '1px solid var(--th-border-subtle)', borderRadius: 3,
      padding: '8px 10px', marginBottom: 8, background: 'var(--th-row-even)',
    }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 'var(--fs-small)', color: 'var(--th-navy)', flex: 1 }}>
          {p.label || p.product?.label || 'Item'}
        </span>
        <button
          type="button" onClick={() => onRemove(p.id)} disabled={busy}
          title="Remove this item"
          style={{ ...BTN_GLYPH, color: 'var(--th-text-secondary)', cursor: busy ? 'default' : 'pointer' }}
        >{DELETE}</button>
      </div>
      {p.configuration.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          {p.configuration.map((c, i) => (
            <ParamEditor
              key={i} p={c} disabled={busy}
              onCommit={value => onConfigure(p.id, c.name, value)}
            />
          ))}
        </div>
      )}
      {p.description && (
        <Text kind="note" as="div" style={{ maxWidth: 620, lineHeight: 1.45 }}>
          {p.description}
        </Text>
      )}
      <ModelPanel placementId={p.id} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add a product — category, then product.
//
// ⚠ AUTO-ADVANCE IS NOT OPTIONAL HERE. The house rule (cascade-signal.js, and
// the gate check-cascade.mjs behind it) is that a cascade opens the next level
// the moment the previous one is answered — never "pick, then click again".
// `openSignal` is a counter the parent bumps; CascadeDropdown opens when it
// changes. Two levels rather than the internal screen's four: the customer picks
// from finished products, and a four-deep walk through our catalog structure is
// the internal vocabulary this whole screen exists to keep out.
// ---------------------------------------------------------------------------
function AddProduct({ products, onAdd, busy }) {
  const [cat, setCat] = useState('');
  const [productId, setProductId] = useState('');
  // ⚠ THE HOUSE HOOK, not a local counter. check-cascade.mjs rejects a
  // hand-rolled openSignal, and it was right to: a bare counter opens whatever
  // level happens to read it, while signalFor(key) targets ONE level, so a reset
  // or a second cascade on the same screen cannot prime the wrong dropdown.
  const { advance, signalFor } = useCascadeSignal();

  const categories = useMemo(() => {
    const seen = new Map();
    for (const p of products) {
      const code = p.l1_code || '—';
      if (!seen.has(code)) seen.set(code, { code, label: p.l1_label || '' });
    }
    return [...seen.values()];
  }, [products]);

  const inCat = useMemo(
    // `code` is the VALUE CascadeDropdown hands back, and it must be the product id
    // — codes are not unique enough to key a write on. `display` is what the mono
    // column shows, so the customer reads PTD.STG.DEG and not the row id 805.
    () => products
      .filter(p => (p.l1_code || '—') === cat)
      .map(p => ({ code: String(p.id), display: p.code, label: p.label })),
    [products, cat],
  );

  // One category and nothing to choose between — answer it and move on rather
  // than asking a question with a single answer.
  useEffect(() => {
    if (!cat && categories.length === 1) { setCat(categories[0].code); advance('product'); }
  }, [categories, cat, advance]);

  if (!products.length) {
    return (
      <Text kind="note" as="div" style={{ fontStyle: 'italic' }}>
        No products are available to place yet.
      </Text>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <CascadeSelect
        wrap={false}
        levels={[
          {
            options: categories,
            value: cat,
            onChange: v => { setCat(v); setProductId(''); advance('product'); },
            placeholder: '— area —',
            ariaLabel: 'Area',
            width: 150,
          },
          {
            options: inCat,
            value: productId,
            onChange: setProductId,
            placeholder: '— pick a product —',
            ariaLabel: 'Product',
            disabled: !cat,
            width: 220,
            openSignal: signalFor('product'),
          },
        ]}
      />
      <button
        type="button"
        disabled={!productId || busy}
        onClick={() => { onAdd(Number(productId)); setProductId(''); }}
        style={{ ...BTN_PRIMARY, fontFamily: 'inherit', ...((!productId || busy) ? BTN_DISABLED : null) }}
      >Add</button>
    </div>
  );
}

function AddSection({ onAdd, busy, parentLabel }) {
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const submit = () => {
    if (!code.trim() && !label.trim()) return;
    onAdd({ code: code.trim() || null, label: label.trim() || null });
    setCode(''); setLabel('');
  };
  const field = {
    fontSize: 'var(--fs-small)', fontFamily: 'inherit', padding: '2px 6px',
    border: 'var(--tg-filter-border)', borderRadius: 3,
  };
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <input value={code} onChange={e => setCode(e.target.value)} placeholder="Code"
             onKeyDown={e => e.key === 'Enter' && submit()} style={{ ...field, width: 70 }} />
      <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Section name"
             onKeyDown={e => e.key === 'Enter' && submit()} style={{ ...field, width: 240 }} />
      <button
        type="button" onClick={submit} disabled={busy}
        style={{ ...BTN_SECONDARY, fontFamily: 'inherit', ...(busy ? BTN_DISABLED : null) }}
      >Add section{parentLabel ? ` under ${parentLabel}` : ''}</button>
    </div>
  );
}

export default function CustomerConfigureView({ view, setView, activeProjectId }) {
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  const load = useCallback(async () => {
    if (!activeProjectId) { setData(null); return; }
    setLoading(true); setError(null);
    try {
      setData(await call(`/api/customer/projects/${encodeURIComponent(activeProjectId)}`));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [activeProjectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    call('/api/customer/products/placeable')
      .then(rows => { if (alive) setProducts(Array.isArray(rows) ? rows : []); })
      .catch(e => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, []);

  // Every mutation reloads the whole project rather than patching local state.
  // The payload is one request and the customer's own project is small; two
  // copies of the truth is how the internal screens grew their hardest bugs.
  const mutate = useCallback(async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); await load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }, [load]);

  const functions = data?.functions || [];
  const treeNodes = useMemo(() => toTreeNodes(functions), [functions]);
  const selected = selectedNodeId ? findFn(functions, selectedNodeId) : null;
  const shown = selected ? [selected] : functions;

  const addSection = (fields) => mutate(() => call(
    `/api/customer/projects/${encodeURIComponent(activeProjectId)}/functions`,
    json({ ...fields, parent_id: selected ? selected.id : null }),
  ));
  const removeSection = (id) => mutate(() => call(`/api/customer/functions/${id}`, { method: 'DELETE' }));
  const addProduct = (fnId, productId) => mutate(() => call(
    `/api/customer/functions/${fnId}/placements`, json({ product_id: productId }),
  ));
  const removeProduct = (id) => mutate(() => call(`/api/customer/placements/${id}`, { method: 'DELETE' }));
  const configure = (placementId, name, value) => mutate(() => call(
    `/api/customer/placements/${placementId}`, patch({ actual_values: { [name]: value } }),
  ));

  const header = (
    <div style={{ marginBottom: 14 }}>
      <SectionTitle>{data ? `${data.project.code} — ${data.project.customer}` : 'Project'}</SectionTitle>
      <Text kind="note" as="div">
        {selected ? `${selected.code} ${selected.label || ''}` : 'Build your project — add sections, then place products in them.'}
      </Text>
    </div>
  );

  const muted = (t) => <Text kind="note" as="div">{t}</Text>;

  let body;
  if (!activeProjectId) {
    body = muted('Pick a project in the banner above to start building it.');
  } else if (loading && !data) {
    body = muted('Loading…');
  } else {
    body = (
      <>
        {error && <Notice kind="warning" onDismiss={() => setError(null)}>{error}</Notice>}
        {shown.length === 0 && muted('Nothing here yet. Add your first section below.')}
        {shown.map(fn => (
          <section key={fn.id} style={{ marginBottom: 20 }}>
            <div style={{
              display: 'flex', gap: 8, alignItems: 'baseline',
              borderBottom: '2px solid var(--th-border)', paddingBottom: 3, marginBottom: 8,
            }}>
              <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', color: 'var(--th-navy)' }}>
                {fn.code}
              </span>
              <Text kind="small" bold style={{ flex: 1 }}>{fn.label || ''}</Text>
              <button
                type="button" onClick={() => removeSection(fn.id)} disabled={busy}
                title="Remove this section"
                style={{ ...BTN_GLYPH, color: 'var(--th-text-secondary)', cursor: busy ? 'default' : 'pointer' }}
              >{DELETE}</button>
            </div>
            {(fn.placements || []).map(p => (
              <PlacementBlock
                key={p.id} p={p} busy={busy}
                onConfigure={configure}
                onRemove={removeProduct}
              />
            ))}
            <div style={{ marginTop: 6 }}>
              <AddProduct products={products} busy={busy} onAdd={id => addProduct(fn.id, id)} />
            </div>
            {(fn.children || []).length > 0 && (
              <Text kind="note" as="div" style={{ marginTop: 8, marginLeft: 14 }}>
                {fn.children.length} sub-section{fn.children.length === 1 ? '' : 's'} — select one in the tree to work in it.
              </Text>
            )}
          </section>
        ))}
        <div style={{ marginTop: 18, paddingTop: 12, borderTop: '1px solid var(--th-border-subtle)' }}>
          <AddSection onAdd={addSection} busy={busy} parentLabel={selected ? (selected.label || selected.code) : ''} />
        </div>
      </>
    );
  }

  return (
    <HierarchyWorkspace
      treeNodes={treeNodes}
      selectedNodeId={selectedNodeId}
      onSelectNode={setSelectedNodeId}
      treeHeader={<ViewSwitcher view={view} setView={setView} onSelectRoot={() => setSelectedNodeId(null)} />}
      filterPlaceholder="Filter sections…"
      storageKey="customer-configure"
      mainHeader={header}
    >
      <div style={{ maxWidth: 780 }}>{body}</div>
    </HierarchyWorkspace>
  );
}
